import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  BoltIcon,
  ChartBarIcon,
  ChartBarUpIcon,
  CogIcon,
  DocumentIcon,
  GlobeAltIcon,
  LightBulbIcon,
  MailIcon,
  PaperAirplaneIcon,
  TargetIcon,
  UsersIcon,
} from './Icons';
import {
  NavIconKey,
  NavRole,
  ResolvedNavItem,
  ResolvedNavSection,
  collectExpandableGroupIds,
  resolveNavigationSections,
} from '../config/navigation';
import { useEntitlements } from '../contexts/EntitlementContext';

const navIcons: Record<NavIconKey, React.ReactNode> = {
  dashboard: <ChartBarIcon size={5} />,
  leads: <BoltIcon size={5} />,
  imports: <DocumentIcon size={5} />,
  prospects: <TargetIcon size={5} />,
  clients: <UsersIcon size={5} />,
  upsell: <ArrowTrendingUpIcon size={5} />,
  campaigns: <PaperAirplaneIcon size={5} />,
  email: <MailIcon size={5} />,
  performance: <ChartBarUpIcon size={5} />,
  chatbot: <LightBulbIcon size={5} />,
  website: <GlobeAltIcon size={5} />,
  settings: <CogIcon size={5} />,
};

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const itemClassName = (current: boolean, nested = false) =>
  [
    'flex items-center rounded text-sm font-medium transition-colors',
    nested ? 'px-3 py-2 pl-9' : 'px-3 py-2.5',
    current
      ? nested
        ? 'bg-sidebar-active/80 text-sidebar-fg'
        : 'bg-sidebar-active text-sidebar-fg border-l-2 border-primary -ml-0.5 pl-3.5'
      : 'text-sidebar-fg-muted hover:bg-sidebar-hover hover:text-sidebar-fg',
  ].join(' ');

interface SidebarNavProps {
  role: NavRole | undefined;
  onNavigate?: () => void;
}

const NavLeaf: React.FC<{
  item: ResolvedNavItem;
  nested?: boolean;
  onNavigate: () => void;
}> = ({ item, nested, onNavigate }) => {
  if (!item.href) return null;

  const icon = item.icon ? navIcons[item.icon] : null;

  return (
    <Link to={item.href} onClick={onNavigate} className={itemClassName(item.current, nested)}>
      {icon && !nested ? <span className="mr-3 inline-flex items-center">{icon}</span> : null}
      {item.name}
    </Link>
  );
};

const ParentRow: React.FC<{
  item: ResolvedNavItem;
  expanded: boolean;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}> = ({ item, expanded, onToggle, onNavigate }) => {
  const icon = item.icon ? navIcons[item.icon] : null;

  return (
    <div className={itemClassName(item.current)}>
      {item.href ? (
        <Link to={item.href} onClick={onNavigate} className="flex min-w-0 flex-1 items-center">
          {icon ? <span className="mr-3 inline-flex items-center">{icon}</span> : null}
          <span className="truncate">{item.name}</span>
        </Link>
      ) : (
        <span className="flex min-w-0 flex-1 items-center">
          {icon ? <span className="mr-3 inline-flex items-center">{icon}</span> : null}
          <span className="truncate">{item.name}</span>
        </span>
      )}
      <button
        type="button"
        onClick={() => onToggle(item.id)}
        className="ml-1 rounded p-1 text-sidebar-fg-muted hover:bg-sidebar-hover hover:text-sidebar-fg"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.name}`}
      >
        <ChevronIcon expanded={expanded} />
      </button>
    </div>
  );
};

const NavItem: React.FC<{
  item: ResolvedNavItem;
  expandedGroups: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}> = ({ item, expandedGroups, onToggle, onNavigate }) => {
  const hasChildren = Boolean(item.children?.length);
  const expanded = expandedGroups.has(item.id);

  if (hasChildren) {
    return (
      <li>
        <ParentRow item={item} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />
        {expanded && item.children && (
          <ul className="mt-0.5 space-y-0.5">
            {item.children.map((child) => (
              <li key={child.id}>
                <NavLeaf item={child} nested onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      <NavLeaf item={item} onNavigate={onNavigate} />
    </li>
  );
};

const NavSectionBlock: React.FC<{
  section: ResolvedNavSection;
  expandedGroups: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: () => void;
}> = ({ section, expandedGroups, onToggle, onNavigate }) => (
  <div className={section.label ? 'mb-4' : 'mb-1'}>
    {section.label ? (
      <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-sidebar-fg-muted/80">
        {section.label}
      </div>
    ) : null}
    <ul className="space-y-0.5 px-3">
      {section.items.map((item) => (
        <NavItem
          key={item.id}
          item={item}
          expandedGroups={expandedGroups}
          onToggle={onToggle}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  </div>
);

const SidebarNav: React.FC<SidebarNavProps> = ({ role, onNavigate }) => {
  const location = useLocation();
  const { entitlements } = useEntitlements();
  const sections = useMemo(
    () => resolveNavigationSections(role, location.pathname, location.search, entitlements),
    [role, location.pathname, location.search, entitlements]
  );

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const activeGroups = collectExpandableGroupIds(sections);
    if (activeGroups.length === 0) return;

    setExpandedGroups((prev) => {
      const next = new Set(prev);
      for (const id of activeGroups) next.add(id);
      return next;
    });
  }, [sections]);

  const handleNavigate = () => onNavigate?.();

  const handleToggle = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <nav className="flex-1 overflow-y-auto py-4">
      {sections.map((section) => (
        <NavSectionBlock
          key={section.id}
          section={section}
          expandedGroups={expandedGroups}
          onToggle={handleToggle}
          onNavigate={handleNavigate}
        />
      ))}
    </nav>
  );
};

export default SidebarNav;
